/**
 * Utility functions for handling day ordering based on user preferences
 */

export type WeekStartDay = 'Sunday' | 'Monday';

/**
 * Base days array in natural order
 */
// const ALL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Gets the ordered days array based on week start preference
 * @param weekStartsOn - Which day the week should start on
 * @param includeWeekends - Whether to include weekends (Saturday and Sunday)
 * @returns Ordered array of days
 */
export function getOrderedDays(
  weekStartsOn: WeekStartDay = 'Monday',
  includeWeekends: boolean = true
): string[] {
  let orderedDays: string[];
  
  if (weekStartsOn === 'Monday') {
    orderedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  } else {
    orderedDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  }
  
  if (!includeWeekends) {
    orderedDays = orderedDays.filter(day => day !== 'Saturday' && day !== 'Sunday');
  }
  
  return orderedDays;
}

/**
 * Gets workdays only (Monday-Friday) in the preferred order
 * @param weekStartsOn - Which day the week should start on
 * @returns Ordered array of workdays
 */
export function getWorkdays(weekStartsOn: WeekStartDay = 'Monday'): string[] {
  return getOrderedDays(weekStartsOn, false);
}

/**
 * Gets weekends only (Saturday and Sunday) in the preferred order
 * @param weekStartsOn - Which day the week should start on
 * @returns Ordered array of weekend days
 */
export function getWeekends(weekStartsOn: WeekStartDay = 'Monday'): string[] {
  if (weekStartsOn === 'Monday') {
    return ['Saturday', 'Sunday'];
  } else {
    return ['Sunday', 'Saturday'];
  }
}

/**
 * Reorders a timetable object to match the preferred day order
 * @param timetable - Object with days as keys
 * @param weekStartsOn - Which day the week should start on
 * @returns Reordered timetable object
 */
export function reorderTimetable<T>(
  timetable: Record<string, T>,
  weekStartsOn: WeekStartDay = 'Monday'
): Record<string, T> {
  const orderedDays = getOrderedDays(weekStartsOn, true);
  const reorderedTimetable: Record<string, T> = {};
  
  orderedDays.forEach(day => {
    if (timetable[day]) {
      reorderedTimetable[day] = timetable[day];
    }
  });
  
  return reorderedTimetable;
}

/**
 * Gets the day index based on the preferred week start
 * @param day - The day to get index for
 * @param weekStartsOn - Which day the week should start on
 * @returns Index of the day (0-6)
 */
export function getDayIndex(day: string, weekStartsOn: WeekStartDay = 'Monday'): number {
  const orderedDays = getOrderedDays(weekStartsOn, true);
  return orderedDays.indexOf(day);
}

/**
 * Gets the next day in the preferred week order
 * @param currentDay - Current day
 * @param weekStartsOn - Which day the week should start on
 * @returns Next day in the week
 */
export function getNextDay(currentDay: string, weekStartsOn: WeekStartDay = 'Monday'): string {
  const orderedDays = getOrderedDays(weekStartsOn, true);
  const currentIndex = orderedDays.indexOf(currentDay);
  const nextIndex = (currentIndex + 1) % orderedDays.length;
  return orderedDays[nextIndex];
}

/**
 * Gets the previous day in the preferred week order
 * @param currentDay - Current day
 * @param weekStartsOn - Which day the week should start on
 * @returns Previous day in the week
 */
export function getPreviousDay(currentDay: string, weekStartsOn: WeekStartDay = 'Monday'): string {
  const orderedDays = getOrderedDays(weekStartsOn, true);
  const currentIndex = orderedDays.indexOf(currentDay);
  const prevIndex = currentIndex === 0 ? orderedDays.length - 1 : currentIndex - 1;
  return orderedDays[prevIndex];
}